<template lang="html">
  <div class="panel">
    <div class="panel-heading bg-green">
      <span class="cap-icon cap-icon_3x cap-icon-wallet-lock"></span>
      <span>{{ $t("Способы подтверждения") }}</span>
    </div>
    <div class="panel-body no-pd">
      <transition>
        <div class="loading-block" v-if="isLoading">
          <circle-loader size="40px">{{ $t("Загрузка настроек безопасности") }}...</circle-loader>
        </div>
      </transition>
      <transition name="component-fade" mode="in-out">
        <div class="security_types__table" v-if="!isLoading">
          <div class="security_types__type">
            <div class="security_types__type__heading">
              <h3>
                <google-code-logo :size="35" :colored="true"></google-code-logo>
                <span>{{ $t("Google 2FA") }}</span>
              </h3>
              <div>
                <setup-toggler :value="google.isEnabled" @input="google.click.call(google)"
                               :waiting="google.waiting"></setup-toggler>
              </div>
            </div>
            <div class="security_types__type__body">
              <p class="text-14">{{ $t("google.line1") }}</p>
            </div>
            <div class="security_types__type__footer">
              <a class="btn btn-md btn-success-outline" :href="google.url" @click.prevent="google.click.call(google)">
                {{ $t("Настроить") }}
              </a>
            </div>
          </div>
          <div class="security_types__type" v-if="isSmsSettingsAvail">
            <div class="security_types__type__heading">
              <h3>
                <span class="cap-icon cap-icon_3x cap-icon-sms-inform text-success"></span>
                <span>{{ $t("SMS подтверждение") }}</span>
              </h3>
              <div>
                <setup-toggler :value="sms.isEnabled" @input="sms.click.call(sms)"
                               :waiting="sms.waiting"></setup-toggler>
              </div>
            </div>
            <div class="security_types__type__body">
              <p class="text-14">{{ $t("sms.line1") }}</p>
            </div>
            <div class="security_types__type__footer">
              <a class="btn btn-md btn-success-outline" :href="sms.url" @click.prevent="sms.click.call(sms)">
                {{ $t("Настроить") }}
              </a>
            </div>
          </div>
          <div class="security_types__type">
            <div class="security_types__type__heading">
              <h3>
                <span class="cap-icon cap-icon_3x cap-icon-email-change text-success"></span>
                <span>{{ $t("Сертификат") }}</span>
              </h3>
              <div>
                <setup-toggler :value="cert.isEnabled" @input="cert.click.call(cert)"
                               :waiting="cert.waiting"></setup-toggler>
              </div>
            </div>
            <div class="security_types__type__body">
              <p class="text-14">{{ $t("cert.line1") }}</p>
            </div>
            <div class="security_types__type__footer">
              <a class="btn btn-md btn-success-outline" :href="cert.url" @click.prevent="cert.click.call(cert)">
                {{ $t("Настроить") }}
              </a>
            </div>
          </div>
          <div class="security_types__type">
            <div class="security_types__type__heading">
              <h3>
                <span class="cap-icon cap-icon_3x cap-icon-passcode text-success"></span>
                <span>{{ $t("Таблица паролей") }}</span>
              </h3>
              <div>
                <setup-toggler :value="table.isEnabled" @input="table.click.call(table)"
                               :waiting="table.waiting"></setup-toggler>
              </div>
            </div>
            <div class="security_types__type__body">
              <p class="text-14">{{ $t("table.line1") }}</p>
            </div>
            <div class="security_types__type__footer">
              <a class="btn btn-md btn-success-outline" :href="table.url" @click.prevent="table.click.call(table)">
                {{ $t("Настроить") }}
              </a>
            </div>
          </div>
          <div class="security_types__type" v-if="isPinSettingsAvail">
            <div class="security_types__type__heading">
              <h3>
                <span class="cap-icon cap-icon_3x cap-icon-pin-lock text-success"></span>
                <span>{{ $t("Платежный PIN-код") }}</span>
              </h3>
              <div>
                <setup-toggler :value="pin.isEnabled" @input="pin.click.call(pin)"
                               :waiting="pin.waiting"></setup-toggler>
              </div>
            </div>
            <div class="security_types__type__body">
              <p class="text-14">{{ $t("pin.line1") }}</p>
              <p class="pin-alert">
                <i18n tag="span" path="pin.alert">
                  <template v-slot:g2fa>
                    <strong>Google 2FA</strong>
                  </template>
                </i18n>
                <!-- Commented out in original
                <i18n tag="span" path="pin.alert2">
                  <template v-slot:g2fa>
                    <strong>Google 2FA</strong>
                  </template>
                </i18n>
                -->
              </p>
            </div>
            <div class="security_types__type__footer">
              <a class="btn btn-md btn-warning" :href="pin.url" @click.prevent="pin.click.call(pin)">
                {{ $t("Отключить") }}
              </a>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { reactive, computed, onMounted, ComputedRef } from "vue";
import SetupToggler from "@/views/pages/security/setup/SetupToggler.vue";
import { VerificationsPanelModel } from "@/views/pages/security/security";
import { VerificationTypes } from "@/models/verification/verification";
import GoogleCodeLogo from "@/components/forms/googlecode/GoogleCodeLogo.vue";
import CircleLoader from "@/components/loaders/CircleLoader.vue";

// Components registration (automatically handled in Vue 3 script setup, but needed in Vue 2.7)
const components = {
  CircleLoader,
  GoogleCodeLogo,
  SetupToggler
};

// Interface for service block
interface ServiceBlock {
  service: VerificationTypes;
  model: VerificationsPanelModel;
  url: string;
  waiting: boolean;
  isEnabled: ComputedRef<boolean>;
  click: () => void;
}

class ServiceBlockModel {
  service: VerificationTypes = VerificationTypes.SMS;
  waiting = false;
  model: VerificationsPanelModel;
  url = "/security/sms";

  constructor(service: VerificationTypes,
              model: VerificationsPanelModel,
              url: string) {
    this.service = service;
    this.model = model;
    this.url = url;
  }

  get isEnabled() {
    return this.model.settings.verification[this.service];
  }

  click() {
    this.waiting = true;
    setTimeout(() => {
      window.location.href = this.url;
    }, 500);
  }
}

// Create model
const model = reactive<VerificationsPanelModel>(new VerificationsPanelModel());

// Create service blocks
const sms = new ServiceBlockModel(VerificationTypes.SMS, model as VerificationsPanelModel, "/security/sms/");
const cert = new ServiceBlockModel(VerificationTypes.CERTIFICATE, model as VerificationsPanelModel, "/security/cert/");
const pin = new ServiceBlockModel(VerificationTypes.PIN, model as VerificationsPanelModel, "/security/pin/");
const table = new ServiceBlockModel(VerificationTypes.TABLECODE, model as VerificationsPanelModel, "/security/table/");
const google = new ServiceBlockModel(VerificationTypes.GOOGLECODE, model as VerificationsPanelModel, "/security/googlecode/");

// Computed properties
const isLoading = computed<boolean>(() => !model.loadingStatus.isLoaded);
const isPinSettingsAvail = computed<boolean>(() =>
  model.loadingStatus.isLoaded && model.settings.pinSettingsAvail
);
const isSmsSettingsAvail = computed<boolean>(() =>
  model.loadingStatus.isLoaded && model.settings.smsSettingsAvail
);

// Lifecycle hooks
onMounted(() => {
  model.loadSettings();
});
</script>

<style scoped lang="less">
.loading-block {
  min-height: 200px;
  margin: 30px 30px;
  align-items: center;
  display: grid;
  justify-items: center;
}

.panel-body {
  min-height: 200px;
}

.security_types__table {
  display: grid;
  grid-gap: 0;
  grid-template-columns: 1fr 1fr;

  .security_types__type {
    display: grid;
    grid-template-rows: min-content auto min-content;
    grid-auto-flow: row;
    align-items: flex-start;

    border-right: 1px solid #EEEEEE;
    //border-top: 1px solid #EEEEEE;
    border-bottom: 1px solid #EEEEEE;
    padding: 15px 25px 25px 25px;

    &:nth-child(1), &:nth-child(2) {
      border-top: none;
    }

    &:nth-child(2n+1):nth-last-child(-n+2),
    &:nth-child(2n+1):nth-last-child(-n+2) ~ .security_types__type {
      border-bottom: none;
    }

    .security_types__type__ {
      &heading {
        display: grid;
        align-items: center;
        grid-template-columns: auto min-content;
        margin-top: 15px;

        h3 {
          display: grid;
          margin: 0;
          grid-gap: 15px;
          align-items: center;
          grid-template-columns: min-content auto;
        }
      }

      &body {
        padding: 25px 0 0 0;

        .pin-alert {
          border-radius: 4px;
          padding: 10px;
          margin: 20px 0 10px 0;
          border: 1px solid #ada00b;
          background: #fffaa4;
        }
      }

      &footer {
        padding: 15px 0 0 0;
      }
    }
  }
}

.component-fade-enter-active, .component-fade-leave-active {
  transition: opacity .3s ease;
}

.component-fade-enter, .component-fade-leave-to
  /* .component-fade-leave-active до версии 2.1.8 */ {
  opacity: 0;
}
</style>

<i18n>
{
  "en": {
    "Платежный PIN-код": "PIN-password",
    "Таблица паролей": "Table of one-time codes",
    "Способы подтверждения": "Verification types",
    "sms": {
      "line1": "Setup one of the safest methods to confirm financial transactions via SMS passwords."
    },
    "cert": {
      "line1": "Generate and save the Certificate to make and confirm financial transactions."
    },
    "google": {
      "line1": "Setup Google 2FA for login security and financial transactions confirmations."
    },
    "table": {
      "line1": "Generate and save the Table of one-time codes in PDF format to make and confirm financial transactions."
    },
    "pin": {
      "line1": "Create reusable payment PIN-password.",
      "alert": "Please use a much safer verification method {g2fa} and disable your PIN."
    },
    "SMS подтверждение": "SMS verification",
    "Включить": "Activate",
    "Отключить": "Disable",
    "Сертификат": "Certificate",
    "Настроить": "Configure",
    "Загрузка настроек безопасности": "Loading security settings"
  },
  "ru": {
    "sms": {
      "line1": "Настройте один из самых безопасных методов подтверждения финансовых операций с помощью SMS паролей."
    },
    "cert": {
      "line1": "Сгенерируйте и сохраните Сертификат для совершения и подтверждения финансовых операций."
    },
    "google": {
      "line1": "Настройте двухфакторную аутентификацию Google для защиты входа в аккаунт и подтверждения платежей."
    },
    "table": {
      "line1": "Сгенерируйте и сохраните Таблицу одноразовых кодов в формате PDF для совершения и подтверждения финансовых операций."
    },
    "pin": {
      "line1": "Создайте многоразовый платежный пароль.",
      "alert": "Пожалуйста, перейдите на гораздо более безопасный способ подтверждения {g2fa} и отключите PIN-код."
    }
  }
}
</i18n>
